/**
 * Designing a certificate.
 *
 * The preview on this page and the PDF a trainee downloads are drawn from the
 * same description — the list of lines below — so there is only one thing to
 * get wrong. Everything is placed as a share of the page rather than in
 * pixels, which is why the preview can be any size and still be honest.
 *
 * The typefaces are the same files too: the PDF embeds them and this page
 * loads them from the server, so the letters here are the letters there.
 * What can still differ by a hair is where a long line wraps, which is why
 * the name and the course are set to shrink onto one line instead.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api, ApiError, uploadFile, CERTIFICATE_PAGE, templatePdfUrl,
  type CertificateBlock, type CertificateLayout, type CertificateTemplateDetail,
} from '../../api';

/**
 * Type sizes are given as a share of the page width rather than in pixels, so
 * the preview is right at whatever width the column ends up being — on a
 * laptop, on a phone, beside a wide panel or under a narrow one. `cqw` is one
 * per cent of the sheet's own width, which is the same thing the PDF means by
 * a point on an 841.89pt page.
 */
const cqw = (points: number) => `${(points / CERTIFICATE_PAGE.width) * 100}cqw`;

const blank = (): CertificateBlock => ({ text: 'New line', top: 50, size: 16, align: 'center', font: 'sans' });

/** The same choice of file the server makes (lib/certificate-fonts.ts). */
const fontKey = (b: CertificateBlock) => {
  if (b.font === 'sans') return b.bold ? 'sans-bold' : 'sans';
  if (b.bold && b.italic) return 'serif-bold-italic';
  if (b.bold) return 'serif-bold';
  if (b.italic) return 'serif-italic';
  return 'serif';
};

/**
 * "Shrink to fit", for the preview. The PDF and the certificate page work it
 * out on the server from the font files; here the browser measures the same
 * font, already loaded, so the preview shows the same decision. Never below
 * 55% — the same floor the server keeps.
 */
let measurer: CanvasRenderingContext2D | null = null;
function fittedSize(text: string, family: string, size: number, widthPt: number): number {
  measurer ??= document.createElement('canvas').getContext('2d');
  if (!measurer) return size;
  measurer.font = `${size}px "${family}"`;
  const w = measurer.measureText(text).width;
  return w <= widthPt ? size : Math.max(size * 0.55, Math.floor((size * widthPt / w) * 10) / 10);
}

export function CertificateDesigner() {
  const id = Number(useParams().id);
  const [tpl, setTpl] = useState<CertificateTemplateDetail | null>(null);
  const [starter, setStarter] = useState<CertificateLayout | null>(null);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<CertificateBlock[]>([]);
  // "Shrink to fit" measures the real typeface, which arrives after the first
  // drawing; measure again once it has, or the preview fits the fallback font.
  const [, setFontsReady] = useState(false);
  useEffect(() => { void document.fonts.ready.then(() => setFontsReady(true)); }, [blocks.length]);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(0);

  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get<{
        template: CertificateTemplateDetail; starter: CertificateLayout; knownTags: string[];
      }>(`/admin/certificates/templates/${id}`);
      setTpl(res.template);
      setStarter(res.starter);
      setKnownTags(res.knownTags);
      setName(res.template.name);
      setBlocks(res.template.layout?.blocks ?? []);
      setDirty(false);
    } catch (e) { setError((e as ApiError).message); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const edit = (i: number, patch: Partial<CertificateBlock>) => {
    setBlocks((bs) => bs.map((b, n) => (n === i ? { ...b, ...patch } : b)));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true); setError(null); setNote(null);
    try {
      await api.patch(`/admin/certificates/templates/${id}`, { name, layout: { blocks } });
      setNote('Saved.'); setDirty(false); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const setBackground = async (file: File) => {
    setBusy(true); setError(null);
    try {
      const up = await uploadFile(file);
      await api.patch(`/admin/certificates/templates/${id}`, { backgroundMediaId: up.id });
      setNote('Background set.'); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  const removeBackground = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/certificates/templates/${id}`, { backgroundMediaId: null });
      setNote('Background removed.'); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  /**
   * Tags nobody can fill in. Shown as you type rather than on save, because
   * the cost of getting this wrong is a certificate that reaches an employer
   * with `{supervisor}` printed on it.
   */
  const unknown = useMemo(() => {
    const out = new Set<string>();
    for (const b of blocks) {
      for (const m of (b.text ?? '').matchAll(/\{([a-zA-Z_]+)\}/g)) {
        if (!knownTags.includes(m[1])) out.add(m[1]);
      }
    }
    return [...out];
  }, [blocks, knownTags]);

  if (!tpl) {
    return (
      <>
        <h1>Certificate design</h1>
        {error ? <p className="auth__error">{error}</p> : <p className="muted">Loading…</p>}
      </>
    );
  }

  const b = blocks[selected];

  return (
    <>
      <p className="crumbs"><Link to="/admin/certificates?tab=templates">Certificates</Link></p>
      <h1>{tpl.name}</h1>

      {error && <p className="auth__error">{error}</p>}
      {note && <p className="notice">{note}</p>}

      {blocks.length === 0 && (
        <div className="notice notice--warn">
          <p>
            This template came from TalentLMS with its own markup. Learnica can show it in a browser, but it
            cannot make a PDF of it — printing it as a file would mean guessing at a design nobody approved.
          </p>
          <p>
            Lay it out here and it becomes a certificate that can be emailed, attached and checked.
          </p>
          <button
            type="button"
            onClick={() => { setBlocks(starter?.blocks ?? [blank()]); setSelected(0); setDirty(true); }}
          >
            Start from a plain design
          </button>
        </div>
      )}

      <div className="designer">
        <div className="designer__paper">
          {/* The same proportions as the page the PDF is drawn on. */}
          <div
            className="sheet-preview"
            style={{
              aspectRatio: `${CERTIFICATE_PAGE.width} / ${CERTIFICATE_PAGE.height}`,
              backgroundImage: tpl.backgroundUrl ? `url("${tpl.backgroundUrl}")` : undefined,
            }}
          >
            {blocks.map((blk, i) => (
              <div
                key={i}
                onClick={() => setSelected(i)}
                className={`sheet-preview__line${i === selected ? ' is-selected' : ''}`}
                style={(() => {
                  const family = `lc-${fontKey(blk)}`;
                  const widthPt = CERTIFICATE_PAGE.width * (1 - 2 * ((blk.margin ?? 8) / 100));
                  const size = blk.fit ? fittedSize(sample(blk.text), family, blk.size, widthPt) : blk.size;
                  return {
                    top: `${blk.top}%`,
                    left: `${blk.margin ?? 8}%`,
                    right: `${blk.margin ?? 8}%`,
                    fontFamily: `"${family}"`,
                    fontSize: cqw(size),
                    lineHeight: 1.22,
                    textAlign: blk.align ?? 'center',
                    color: blk.color ?? '#26242f',
                    whiteSpace: blk.fit ? 'nowrap' : undefined,
                  } as React.CSSProperties;
                })()}
              >
                {sample(blk.text)}
              </div>
            ))}

            {/* Drawn on every certificate whatever the design says, so it is
                shown here too rather than appearing as a surprise in the PDF. */}
            <div className="sheet-preview__stamp">
              Certificate 0000… · issued today · check at {location.host}/verify/…
            </div>
          </div>

          <p className="muted small">
            Shown with stand-in details, in the same typefaces the PDF uses. Open the PDF to see exactly what a
            trainee gets.
          </p>
          <p>
            <a href={templatePdfUrl(id)} target="_blank" rel="noreferrer">Open the PDF</a>
            {dirty && <span className="muted small"> — save first; the PDF is drawn from what is stored.</span>}
          </p>
        </div>

        <div className="designer__panel">
          <label className="form">
            <span>Name</span>
            <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} />
          </label>

          <h2 className="h2">Lines</h2>
          <ol className="designer__lines">
            {blocks.map((blk, i) => (
              <li key={i} className={i === selected ? 'is-selected' : ''}>
                <button type="button" className="linkish" onClick={() => setSelected(i)}>
                  {blk.text.trim().slice(0, 32) || '(empty)'}
                </button>
                <span className="designer__order">
                  <button
                    type="button" disabled={i === 0} title="Move up"
                    onClick={() => { setBlocks(swap(blocks, i, i - 1)); setSelected(i - 1); setDirty(true); }}
                  >↑</button>
                  <button
                    type="button" disabled={i === blocks.length - 1} title="Move down"
                    onClick={() => { setBlocks(swap(blocks, i, i + 1)); setSelected(i + 1); setDirty(true); }}
                  >↓</button>
                  <button
                    type="button" title="Remove this line"
                    onClick={() => {
                      setBlocks(blocks.filter((_, n) => n !== i));
                      setSelected((s) => Math.max(0, s > i ? s - 1 : s));
                      setDirty(true);
                    }}
                  >×</button>
                </span>
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={() => { setBlocks([...blocks, blank()]); setSelected(blocks.length); setDirty(true); }}
          >
            Add a line
          </button>

          {b && (
            <fieldset className="designer__block">
              <legend>Line {selected + 1}</legend>
              <label>
                <span>Text</span>
                <textarea rows={2} value={b.text} onChange={(e) => edit(selected, { text: e.target.value })} />
              </label>

              <div className="row">
                <label>
                  <span>Down the page</span>
                  <input
                    type="range" min={0} max={100} step={0.5} value={b.top}
                    onChange={(e) => edit(selected, { top: Number(e.target.value) })}
                  />
                  <em className="muted small">{b.top}%</em>
                </label>
                <label>
                  <span>Size</span>
                  <input
                    type="number" min={6} max={96} value={b.size}
                    onChange={(e) => edit(selected, { size: Number(e.target.value) })}
                  />
                </label>
              </div>

              <div className="row">
                <label>
                  <span>Align</span>
                  <select
                    value={b.align ?? 'center'}
                    onChange={(e) => edit(selected, { align: e.target.value as CertificateBlock['align'] })}
                  >
                    <option value="left">Left</option>
                    <option value="center">Centre</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label>
                  <span>Colour</span>
                  <input
                    type="color" value={b.color ?? '#12212f'}
                    onChange={(e) => edit(selected, { color: e.target.value })}
                  />
                </label>
                <label>
                  <span>Margin</span>
                  <input
                    type="number" min={0} max={40} value={b.margin ?? 8}
                    onChange={(e) => edit(selected, { margin: Number(e.target.value) })}
                  />
                </label>
              </div>

              <div className="row">
                <label className="check">
                  <input type="checkbox" checked={!!b.bold} onChange={(e) => edit(selected, { bold: e.target.checked })} />
                  Bold
                </label>
                <label className="check">
                  <input type="checkbox" checked={!!b.italic} onChange={(e) => edit(selected, { italic: e.target.checked })} />
                  Italic
                </label>
              </div>

              <div className="row">
                <label>
                  <span>Typeface</span>
                  <select
                    value={b.font ?? 'serif'}
                    onChange={(e) => edit(selected, { font: e.target.value as CertificateBlock['font'] })}
                  >
                    <option value="serif">Serif — for the name, the course and the title</option>
                    <option value="sans">Sans — for everything around them</option>
                  </select>
                </label>
              </div>
              <label className="check">
                <input type="checkbox" checked={!!b.fit} onChange={(e) => edit(selected, { fit: e.target.checked })} />
                Shrink to fit one line
                <em className="muted small"> — for names and course titles, which can be any length</em>
              </label>
              {b.font === 'sans' && b.italic && (
                <p className="muted small">The sans typeface has no italic; this line will be set upright.</p>
              )}
            </fieldset>
          )}

          <h2 className="h2">Background</h2>
          <p className="muted small">
            A JPEG or PNG the size of a landscape A4 page. It fills the page, cropping rather than stretching.
          </p>
          <input
            ref={fileInput} type="file" accept="image/*"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void setBackground(f); }}
          />
          {tpl.hasBackground && (
            <button type="button" className="btn-danger" disabled={busy} onClick={() => void removeBackground()}>
              Remove the background
            </button>
          )}

          <h2 className="h2">Tags you can use</h2>
          <p className="small designer__tags">
            {knownTags.map((t) => (
              <button
                key={t} type="button" className="chip"
                title="Add to this line"
                onClick={() => b && edit(selected, { text: `${b.text}{${t}}` })}
              >{`{${t}}`}</button>
            ))}
          </p>
          {unknown.length > 0 && (
            <p className="warn">
              {unknown.map((t) => `{${t}}`).join(' ')} — Learnica cannot fill {unknown.length === 1 ? 'that in' : 'those in'},
              so this design cannot be saved until {unknown.length === 1 ? 'it is' : 'they are'} changed.
            </p>
          )}

          <div className="designer__save">
            <button type="button" disabled={busy || !dirty} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save the design'}
            </button>
            {dirty && <span className="muted small">Unsaved changes</span>}
          </div>
        </div>
      </div>
    </>
  );
}

const swap = (bs: CertificateBlock[], a: number, b: number) => {
  const out = [...bs];
  [out[a], out[b]] = [out[b], out[a]];
  return out;
};

/** Stand-in values, matching the ones the PDF preview uses. */
const SAMPLE: Record<string, string> = {
  user_name: 'Siti Nurhaliza Rahmawati', full_name: 'Siti Nurhaliza Rahmawati', name: 'Siti Nurhaliza Rahmawati',
  first_name: 'Mariam', last_name: 'Haddad', user_login: 'mariam', username: 'mariam',
  course_name: '1-Introduction to English Language', course: '1-Introduction to English Language',
  course_title: 'Introduction to English Language', course_code: 'EN-101',
  certificate_date: '4 March 2026', issued_date: '4 March 2026', completion_date: '4 March 2026',
  date: '4 March 2026', year: '2026',
  expiration_date: '4 March 2027', expires: '4 March 2027', expiration: '4 March 2027',
  score: '92%', certificate_id: '0000…', certification_id: '0000…',
  company_name: 'Qatar Care', instructor_name: 'Qatar Care Training',
};
const sample = (text: string) =>
  (text ?? '').replace(/\{([a-zA-Z_]+)\}/g, (whole, tag: string) => SAMPLE[tag] ?? whole);
